import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Modal,
  TextField,
  useTheme,
  styled,
} from "@mui/material";
import { tokens } from "../theme";
import secureLocalStorage from "react-secure-storage";
import { jwtDecode } from "jwt-decode";
import {
  ShowVendorCalenderEvents,
  createEventInCalender,
} from "../../../context/Slice/CalenderSlice";
import moment from "moment";

const CustomModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .modal-box": {
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    borderRadius: 8,
  },
}));

const AdminMeetingManagement = () => {
  const dispatch = useDispatch();
  const events = useSelector((state) => state.CalenderR.Data);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [eventData, setEventData] = useState({
    Title: "",
    Description: "",
    Start_Date: "",
    End_Date: "",
    allDay: false,
  });

  const token = secureLocalStorage.getItem("token");

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const id = decoded._id;
        dispatch(ShowVendorCalenderEvents(id));
      } catch (error) {
        console.error("Token decoding failed:", error);
      }
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (events) {
      const formattedEvents = events.map((event) => {
        const start = event.Start_Date
          ? moment(event.Start_Date).toISOString()
          : null;
        const end = event.End_Date
          ? moment(event.End_Date).toISOString()
          : null;
        const proposedDate = event.Proposed_Date
          ? moment(event.Proposed_Date).toISOString()
          : null;

        if (!start && !end && proposedDate) {
          return {
            id: event.id,
            title: event.Title,
            start: proposedDate,
            end: proposedDate,
            allDay: true,
            backgroundColor: "yellow",
          };
        } else if (start && end) {
          return {
            id: event.id,
            title: event.Title,
            start: start,
            end: end,
            allDay: event.Whole_day,
          };
        } else {
          return {
            id: event.id,
            title: event.Title,
            start: start || proposedDate,
            end: end || proposedDate,
            allDay: event.Whole_day,
          };
        }
      });
      setCurrentEvents(formattedEvents);
    }
  }, [events]);

  const handleDateClick = (selected) => {
    setSelectedInfo(selected);
    setEventData({
      ...eventData,
      Start_Date: selected.startStr,
      End_Date: selected.endStr,
      allDay: selected.allDay,
    });
    setOpen(true);
  };

  const handleEventClick = (selected) => {
    if (
      window.confirm(
        `Are you sure you want to delete the event '${selected.event.title}'?`
      )
    ) {
      selected.event.remove();
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name]: value,
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const calendarApi = selectedInfo.view.calendar;
    calendarApi.unselect();

    if (eventData.Title) {
      dispatch(createEventInCalender(eventData)).then(() => {
        calendarApi.addEvent({
          id: `${eventData.Start_Date}-${eventData.Title}`,
          title: eventData.Title,
          description: eventData.Description,
          start: eventData.Start_Date,
          end: eventData.End_Date,
          allDay: eventData.allDay,
        });
        setOpen(false);
        setEventData({
          Title: "",
          Description: "",
          Start_Date: "",
          End_Date: "",
          allDay: false,
        });
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEventData({
      Title: "",
      Description: "",
      Start_Date: "",
      End_Date: "",
      allDay: false,
    });
  };

  const renderEventContent = (eventInfo) => {
    return (
      <div
        style={{
          backgroundColor:
            eventInfo.event.extendedProps.backgroundColor || "inherit",
        }}
      >
        <b>{eventInfo.timeText}</b>
        <i>{eventInfo.event.title}</i>
      </div>
    );
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between">
        {/* CALENDAR SIDEBAR */}
        <Box
          flex="1 1 20%"
          backgroundColor={colors.primary[400]}
          p="15px"
          borderRadius="4px"
        >
          <Typography variant="h5">Events</Typography>
          <List>
            {currentEvents.map((event) => (
              <ListItem
                key={event.id}
                sx={{
                  backgroundColor: colors.greenAccent[500],
                  margin: "10px 0",
                  borderRadius: "2px",
                }}
              >
                <ListItemText
                  primary={event.title}
                  secondary={
                    <>
                      <Typography>
                        Proposed Date:{" "}
                        {event.start
                          ? moment(event.start).format("DD/MM/YY")
                          : "No proposed date"}
                      </Typography>
                      <Typography>
                        Start Date:{" "}
                        {event.start
                          ? moment(event.start).format("DD/MM/YY")
                          : "No start date"}
                      </Typography>
                      <Typography>
                        End Date:{" "}
                        {event.end
                          ? moment(event.end).format("DD/MM/YY")
                          : "No end date"}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* CALENDAR */}
        <Box flex="1 1 100%" ml="15px">
          <FullCalendar
            height="75vh"
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
            ]}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
            }}
            initialView="dayGridMonth"
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            select={handleDateClick}
            eventClick={handleEventClick}
            events={currentEvents}
            eventContent={renderEventContent}
          />
        </Box>
      </Box>

      {/* CUSTOM MODAL FORM */}
      <CustomModal open={open} onClose={handleClose}>
        <Box className="modal-box">
          <Typography variant="h6" component="h2">
            Add New Event
          </Typography>
          <form onSubmit={handleFormSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Event Title"
              name="Title"
              value={eventData.Title}
              onChange={handleFormChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="Description"
              value={eventData.Description}
              onChange={handleFormChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Start Date"
              name="Start_Date"
              type="datetime-local"
              value={eventData.Start_Date}
              onChange={handleFormChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="End Date"
              name="End_Date"
              type="datetime-local"
              value={eventData.End_Date}
              onChange={handleFormChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
            >
              Add Event
            </Button>
          </form>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default AdminMeetingManagement;
